import { algolia_config, cookiesName } from './constants';
import General from './helpers/General';
import Observable from './helpers/Observable';
import { waitForWindowProperty } from './helpers/WaitForWindowProperty';
import './modules/HideHeaderOnScrollTest';
import './modules/PLPGreenTextTest';
import './modules/CustomerCareDrawer';
import initFullsizeTitleTooltipPosition from './modules/FullsizeTitleTooltipPosition';
import Cookie from './helpers/Cookies';
import { isDebugEnabled } from './helpers/Debug';
import { initializeAppMainBootstrap } from './apps-react/AppMain/bootstrap';
import { isConstructorIOIntegrationEnabled } from './modules/external-systems/constructor.io/config';
import { initAutoApplyDiscountCodeTest } from './helpers/AutoApplyDiscountCodeTest';

const PDP_IGNORE_VARIANT_IMAGES_SHOPLIFT_VARIANT_ID =
  '019f621e-17d8-78ee-b07c-223260cd9d8f';
const PDP_IGNORE_VARIANT_IMAGES_SHOPLIFT_TIMEOUT = 10000;
const PDP_IGNORE_VARIANT_IMAGES_TEST_ACTIVE_EVENT =
  'pdpIgnoreVariantImagesTestActive';

const waitForShoplift = () => {
  const startedAt = Date.now();

  return new Promise((resolve) => {
    const check = () => {
      if (typeof window.shoplift?.isHypothesisActive === 'function') {
        resolve(window.shoplift);
        return;
      }

      if (
        Date.now() - startedAt >=
        PDP_IGNORE_VARIANT_IMAGES_SHOPLIFT_TIMEOUT
      ) {
        resolve(null);
        return;
      }

      window.setTimeout(check, 50);
    };

    check();
  });
};

function setupPdpIgnoreVariantImagesTest() {
  const productFull = document.querySelector('#product-full');

  if (!productFull) {
    return;
  }

  if (isDebugEnabled('shoplift')) {
    console.log('[PDP image test] PDP setup detected', {
      shopliftVariantId: PDP_IGNORE_VARIANT_IMAGES_SHOPLIFT_VARIANT_ID,
      url: window.location.href,
    });
  }

  let active = false;

  const setActive = () => {
    if (active) {
      return;
    }

    active = true;
    document.body.dataset.pdpIgnoreVariantImagesTestActive = '';
    if (isDebugEnabled('shoplift')) {
      console.log(
        '[PDP image test] Test active; variant image switching disabled',
        {
          shopliftVariantId: PDP_IGNORE_VARIANT_IMAGES_SHOPLIFT_VARIANT_ID,
        }
      );
    }
    window.dispatchEvent(
      new CustomEvent(PDP_IGNORE_VARIANT_IMAGES_TEST_ACTIVE_EVENT)
    );
  };

  waitForShoplift().then((shoplift) => {
    if (!shoplift) {
      if (isDebugEnabled('shoplift')) {
        console.log('[PDP image test] Shoplift unavailable; test inactive', {
          shopliftVariantId: PDP_IGNORE_VARIANT_IMAGES_SHOPLIFT_VARIANT_ID,
        });
      }
      return;
    }

    Promise.resolve(
      shoplift.isHypothesisActive(PDP_IGNORE_VARIANT_IMAGES_SHOPLIFT_VARIANT_ID)
    )
      .then((isVariantActive) => {
        if (isDebugEnabled('shoplift')) {
          console.log('[PDP image test] Shoplift variant resolved', {
            shopliftVariantId: PDP_IGNORE_VARIANT_IMAGES_SHOPLIFT_VARIANT_ID,
            isVariantActive,
          });
        }
        if (isVariantActive === true) {
          setActive();
        }
      })
      .catch((error) => {
        console.warn('[PDP image test] Shoplift resolution failed', error);
      });
  });
}

General.init();
General.bodyOverflowHiddenController();

if (document.querySelector('[data-sticky], #product-configurator-make-a-set')) {
  import(/* webpackChunkName: "modules_is-sticky" */ './modules/IsSticky').then(
    ({ default: IsSticky }) => {
      new IsSticky().init();
    }
  );
}
initFullsizeTitleTooltipPosition();
setupPdpIgnoreVariantImagesTest();
initAutoApplyDiscountCodeTest();

if (document.querySelector('[data-article-add-to-cart]')) {
  import(
    /* webpackChunkName: "modules_article-add-to-cart" */ './modules/ArticleAddToCart'
  );
}

initializeAppMainBootstrap();

// The shipping estimate is driven by its slot alone, so it runs on the product
// template, the configurator intro and product landing pages alike.
if (document.querySelector('#product-full__ship-information')) {
  import(
    /* webpackChunkName: "pdp-delivery-estimate" */ './modules/pdp/delivery-estimate'
  ).then(({ initDeliveryEstimate }) => initDeliveryEstimate());
}

if (document.querySelector('[data-header-section]')) {
  import(/* webpackChunkName: "modules_header-v2" */ './modules/HeaderV2');
}

let searchReady = null;
let searchAppLoaded = false;
let pendingSearchTrigger = null;
let searchBackgroundLoadScheduled = false;
const searchBackgroundLoadDelay = 1500;

function loadSearchApp() {
  if (!document.querySelector('[data-search-container]')) {
    return Promise.resolve();
  }

  if (!searchReady) {
    searchReady = import(
      /* webpackChunkName: "app_search" */ './apps-react/Search/components/Search/Search'
    )
      .then((module) => {
        const appReady = module?.searchAppReady || Promise.resolve();

        return appReady.then(() => {
          searchAppLoaded = true;
          document.removeEventListener('click', loadSearchOnIntent, true);
          document.removeEventListener(
            'pointerover',
            preloadSearchOnIntent,
            true
          );
          document.removeEventListener('focusin', preloadSearchOnIntent, true);
          document.removeEventListener(
            'touchstart',
            preloadSearchOnIntent,
            true
          );
          return module;
        });
      })
      .catch((error) => {
        searchReady = null;
        throw error;
      });
  }

  return searchReady;
}

function preloadSearchOnIntent(event) {
  const trigger = event.target?.closest?.('.twc-hdr-search-toggle');
  if (!trigger) {
    return;
  }

  loadSearchApp()
    .then((module) =>
      Promise.all([
        module?.preloadSearchUi?.(),
        module?.preloadSearchStyles?.(),
      ])
    )
    .catch(() => {});
}

function loadSearchOnIntent(event) {
  const trigger = event.target?.closest?.('.twc-hdr-search-toggle');
  if (!trigger || searchAppLoaded) {
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();

  pendingSearchTrigger = trigger;
  loadSearchApp().then(() => {
    const nextTrigger = pendingSearchTrigger;
    pendingSearchTrigger = null;

    if (nextTrigger) {
      nextTrigger.click();
    }
  });
}

function scheduleSearchBackgroundLoad() {
  if (searchBackgroundLoadScheduled || searchAppLoaded) {
    return;
  }

  searchBackgroundLoadScheduled = true;

  const loadWhenIdle = () => {
    const idleCallback =
      window.requestIdleCallback ||
      ((callback) => window.setTimeout(callback, 0));

    idleCallback(
      () => {
        if (searchAppLoaded) {
          return;
        }

        loadSearchApp()
          .then((module) => module?.preloadSearchUi?.())
          .catch(() => {});
      },
      { timeout: 3000 }
    );
  };

  const queueBackgroundLoad = () => {
    window.setTimeout(loadWhenIdle, searchBackgroundLoadDelay);
  };

  if (document.readyState === 'complete') {
    queueBackgroundLoad();
  } else {
    window.addEventListener('load', queueBackgroundLoad, { once: true });
  }
}

if (document.querySelector('[data-search-container]')) {
  document.addEventListener('click', loadSearchOnIntent, true);
  document.addEventListener('pointerover', preloadSearchOnIntent, true);
  document.addEventListener('focusin', preloadSearchOnIntent, true);
  document.addEventListener('touchstart', preloadSearchOnIntent, true);
  scheduleSearchBackgroundLoad();
}

import LazySection from './LazySection';
import LazyAssets from './LazyAssets';
import Nav from './modules/Nav';
import CopyToClipboard from './modules/CopyToClipboard';

// import Listrak from "./modules/external-systems/Listrak";
if (document.querySelector('.twc-mnav')) {
  new Nav();
}
new CopyToClipboard();

import Stats from './helpers/Stats';
if (Stats) {
  Stats.listenerOnElements();
  Stats.eventsOnLoad();
}

// import Google from "./stats/modules/Google";
// Google.clickEvent();

(async () => {
  sessionStorage.removeItem('document-lock');

  const observer = new Observable();
  const hasIntersectionObserver = Boolean(window.IntersectionObserver);

  const loadFeatureOnDemand = (
    selector,
    loader,
    { watchForNewNodes = false } = {}
  ) => {
    let ready = null;
    let mutationObserver = null;

    const load = () => {
      if (!ready) {
        ready = loader();
        mutationObserver?.disconnect();
      }
      return ready;
    };

    const observeTarget = (target) => {
      if (hasIntersectionObserver) {
        observer.subscribe(target, load);
      } else {
        load();
      }
    };

    const observeMatches = (root = document) => {
      const matches = [];

      if (root.matches?.(selector)) {
        matches.push(root);
      }

      root.querySelectorAll?.(selector).forEach((node) => {
        matches.push(node);
      });

      matches.forEach(observeTarget);
      return matches.length > 0;
    };

    const hasCurrentMatches = observeMatches();

    if (!hasCurrentMatches && !watchForNewNodes) {
      return;
    }

    if (watchForNewNodes && !ready) {
      if (!window.MutationObserver) {
        if (!hasCurrentMatches) {
          load();
        }
        return;
      }

      mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            observeMatches(node);
          });
        });
      });

      mutationObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });

      if (!hasCurrentMatches) {
        return;
      }
    }

    if (hasIntersectionObserver) {
      return;
    } else {
      load();
    }
  };

  new LazySection(hasIntersectionObserver ? observer : false, Stats);
  const assets = new LazyAssets(hasIntersectionObserver ? observer : false);
  window.loadCssChunk = assets.loadCssChunk;

  if (hasIntersectionObserver) {
    const { default: LazyLoadImages } = await import(
      /* webpackChunkName: "modules_lazy-load-images" */ './modules/LazyLoadImages'
    );
    LazyLoadImages.listener();
    window.LazyLoaderRun = LazyLoadImages.listener;
  } else {
    const { default: LazyLoader } = await import(
      /* webpackChunkName: "modules-old-browsers_lazyLoader" */ `./modules-old-browsers/LazyLoader`
    );
    new LazyLoader();
  }

  if (algolia_config.active) {
    loadFeatureOnDemand('body', () =>
      import(
        /* webpackChunkName: "external_algolia" */ './modules/external-systems/Algolia'
      ).then(({ default: Algolia }) => {
        new Algolia();
      })
    );
  }

  if (isConstructorIOIntegrationEnabled()) {
    loadFeatureOnDemand(
      '[data-constructor-widget-recommendation], [data-constructor-widget-search], [data-constructor-widget-collection]',
      () =>
        import(
          /* webpackChunkName: "external_constructor-widgets" */ './modules/external-systems/constructor.io/widgets'
        ).then(({ initConstructorWidgets }) => {
          initConstructorWidgets();
        }),
      { watchForNewNodes: true }
    );
  }

  if (window?.gld_dy?.enabled === true && window.gld_dy.event) {
    loadFeatureOnDemand(
      '.dy-api, .dy-custom-event--view, [data-dy-event-name]',
      () =>
        import(
          /* webpackChunkName: "external_dy" */ './modules/external-systems/DynamicYield'
        ).then(({ default: DynamicYield }) => {
          new DynamicYield(observer);
        })
    );
  }

  if (window.dyProductCardPerformance) {
    loadFeatureOnDemand('[data-product-card]', () =>
      import(
        /* webpackChunkName: "modules_external-system_dy_product-card" */ './modules/external-systems/dynamic-yield/ProductCard'
      ).then(({ default: DYProductCard }) => {
        DYProductCard.getProductDataPerformance();
      })
    );
  }

  if (window.ab_test_discount) {
    import(
      /* webpackChunkName: "test_discount" */ './modules/tests/Discount'
    ).then(({ default: TestDiscount }) => {
      new TestDiscount(window.ab_test_discount);
    });
  }

  if (window.promotion_percentgage && window.promotion_percentgage > 0) {
    import(
      /* webpackChunkName: "test_promotions" */ './modules/tests/Promos'
    ).then(({ addPromos }) => {
      addPromos();
      window.addPromos = addPromos;

      const productCode = document.querySelector('[data-product-code]');
      const productPromoData = document.querySelector('#product-promo-data');

      if (productCode && productPromoData?.dataset.isPromoProduct === 'true') {
        productCode.style.display = 'none';
      }
    });
  }

  loadFeatureOnDemand(
    '[data-attentive-phone="true"], [data-attentive-submit="true"], [data-attentive-form-inner="true"]',
    () =>
      import(
        /* webpackChunkName: "external_attentive" */ './modules/external-systems/Attentive'
      ).then(({ Attentive }) => {
        Attentive();
      }),
    { watchForNewNodes: true }
  );

  if (!window.GorgiasChat) {
    import(
      /* webpackChunkName: "external_gorgias" */ './modules/external-systems/Gorgias'
    ).then(({ default: Gorgias }) => {
      new Gorgias();
    });
  }

  if (window.promoBarTest) {
    import(
      /* webpackChunkName: "modules_test_sections" */ './modules/tests/Sections'
    ).then(({ default: AnnouncementBar }) => {
      new AnnouncementBar(
        '.shopify-section-gld_promotion-bar--big',
        '.promotion-bar',
        2
      );
    });
  }

  loadFeatureOnDemand('[data-now-number]', () =>
    import(
      /* webpackChunkName: "random-number-cookie" */ './modules/RandomNumberCookie'
    ).then(({ default: RandomNumberCookie }) => {
      new RandomNumberCookie();
    })
  );
  // if (
  //   document.body.classList.contains('template-product') &&
  //   document.getElementById('MainContent')
  // ) {
  //   import(
  //     /* webpackChunkName: "pdp-content-scale" */ './apps-react/AppMain/components/Product/parts/PdpContentScale/PdpContentScale'
  //   ).then(({ default: PdpContentScale }) => {
  //     new PdpContentScale();
  //   });
  // }

  const isPdpRedesignEligible = document.body.classList.contains(
    'pdp-redesign-eligible'
  );

  if (
    isPdpRedesignEligible &&
    (Cookie.get(cookiesName.ux.pdpRedesignTestActive) || window.PdpRedesignTest)
  ) {
    if (!Cookie.get(cookiesName.ux.pdpRedesignTestActive)) {
      Cookie.set({
        name: cookiesName.ux.pdpRedesignTestActive,
        value: true,
        expires: 1,
        path: '/',
      });
    }
  }

  if (
    isPdpRedesignEligible &&
    (Cookie.get(cookiesName.ux.pdpRedesignGalleryTestActive) ||
      window.PdpRedesignGalleryTest)
  ) {
    if (!Cookie.get(cookiesName.ux.pdpRedesignGalleryTestActive)) {
      Cookie.set({
        name: cookiesName.ux.pdpRedesignGalleryTestActive,
        value: true,
        expires: 1,
        path: '/',
      });
    }

    // Inline PdpRedesignGallery logic
    if (window.productData) {
      document.body.dataset.pdpRedesignGalleryTestActive = '';
    }
  }

  if (window.Shopify?.currency?.active === 'USD') {
    waitForWindowProperty('test_price').then((value) => {
      if (value !== undefined) {
        document.body.dataset.testPrice = '';
      }
    });
  }

  if (
    window.show_scroll_progress &&
    document.getElementById('scrollProgressBar')
  ) {
    loadFeatureOnDemand('#scrollProgressBar', () =>
      import(
        /* webpackChunkName: "modules_ScrollProgressBar" */ './modules/ScrollProgressBar'
      ).then(({ default: ScrollProgressBar }) => {
        new ScrollProgressBar();
      })
    );
  }

  loadFeatureOnDemand(
    'localization-form',
    () =>
      import(
        /* webpackChunkName: "modules_LocalizationForm" */ './modules/LocalizationForm'
      ).then(({ default: LocalizationForm }) => {
        if (!customElements.get('localization-form')) {
          customElements.define('localization-form', LocalizationForm);
        }
      }),
    { watchForNewNodes: true }
  );

  loadFeatureOnDemand(
    '[data-svg]',
    () =>
      import(
        /* webpackChunkName: "modules_SvgFromUrlToCode" */ './modules/SvgFromUrlToCode'
      ).then(({ default: SvgFromUrlToCode }) => {
        SvgFromUrlToCode.init();
        window.SvgFromUrlToCodeRun = SvgFromUrlToCode.init;
      }),
    { watchForNewNodes: true }
  );

  loadFeatureOnDemand(
    '[data-hook="stock-today"]',
    () =>
      import(
        /* webpackChunkName: "modules_stock_today" */ './modules/StockToday'
      ).then(({ default: StockToday }) => {
        new StockToday();
      }),
    { watchForNewNodes: true }
  );

  if (window.Shopify?.designMode && window.MutationObserver) {
    const initializeCountdowns = async (root) => {
      if (
        root.querySelector?.(
          '[data-countdown]:not([data-countdown-mode="interval"])'
        )
      ) {
        const { default: CountdownTimer } = await import(
          /* webpackChunkName: "countdown_timer" */ './modules/CountdownTimer'
        );
        new CountdownTimer(root);
      }

      if (
        root.querySelector?.('[data-countdown][data-countdown-mode="interval"]')
      ) {
        const { default: CountdownTimeInterval } = await import(
          /* webpackChunkName: "countdown_time_interval" */ './modules/CountdownTimeInterval'
        );
        new CountdownTimeInterval(root);
      }
    };

    const countdownObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            initializeCountdowns(node).catch(() => {});
          }
        });
      });
    });

    countdownObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  waitForWindowProperty('DYO').then((value) => {
    import(
      /* webpackChunkName: "modules_external-system_dy_events" */ './modules/external-systems/dynamic-yield/Events'
    ).then(({ default: DyEvents }) => {
      new DyEvents({
        stats: Stats,
      });
    });
  });
})();

if (!window.showPreferences) {
  const footerLink = document.querySelector('.isense-reopen-widget-link');
  if (footerLink?.parentNode) {
    footerLink.parentNode.remove();
  }
}
